import React, { useState, useRef } from 'react';
import { useCioPlpContext } from '../../hooks/useCioPlpContext';
import { useOnAddToCart, useOnProductCardClick } from '../../hooks/callbacks';
import ProductSwatch from '../ProductSwatch';
import useProductInfo from '../../hooks/useProduct';
import { concatStyles, getProductCardCnstrcDataAttributes } from '../../utils';
import RenderPropsWrapper from '../RenderPropsWrapper/RenderPropsWrapper';
import { EMITTED_EVENTS } from '../../constants';
/**
 * ProductCard component that has Constructor tracking built-in.
 */
export default function ProductCard(props) {
    const [isRolloverImageShown, setIsRolloverImageShown] = useState(false);
    const cardRef = useRef(null);
    const { item, children } = props;
    const state = useCioPlpContext();
    const productInfo = useProductInfo({ item });
    const { productSwatch, itemName, itemPrice, itemImageUrl, itemUrl, salePrice, hasSalePrice, rolloverImage, } = productInfo;
    if (!state) {
        throw new Error('This component is meant to be used within the CioPlp provider.');
    }
    if (!item.data || !item.itemId || !item.itemName) {
        throw new Error('data, itemId, or itemName are required.');
    }
    const client = state.cioClient;
    const onAddToCart = useOnAddToCart(client, state.callbacks.onAddToCart);
    const { formatPrice } = state.formatters;
    const onClick = useOnProductCardClick(client, state.callbacks.onProductCardClick);
    const cnstrcData = getProductCardCnstrcDataAttributes(productInfo);
    const handleRolloverImageState = (isShown) => {
        setIsRolloverImageShown(isShown);
        if (isShown && rolloverImage) {
            const event = new CustomEvent(EMITTED_EVENTS.PRODUCT_CARD_IMAGE_ROLLOVER, {
                detail: { item },
                bubbles: true,
            });
            cardRef.current?.dispatchEvent(event);
        }
    };
    const onMouseEnter = (event) => {
        if (state.callbacks.onProductCardMouseEnter) {
            state.callbacks.onProductCardMouseEnter(event, item);
        }
        handleRolloverImageState(true);
    };
    const onMouseLeave = (event) => {
        if (state.callbacks.onProductCardMouseLeave) {
            state.callbacks.onProductCardMouseLeave(event, item);
        }
        handleRolloverImageState(false);
    };
    return (React.createElement(RenderPropsWrapper, { props: {
            item,
            productInfo,
            formatPrice,
            onAddToCart,
            onClick,
            onMouseEnter,
            onMouseLeave,
            isRolloverImageShown,
            productCardCnstrcDataAttributes: cnstrcData,
        }, override: children, htmlOverride: state.renderOverrides.productCard?.renderHtml, topLevelAttributes: { ...cnstrcData, className: 'cio-product-card' } },
        React.createElement("a", { ...cnstrcData, className: 'cio-product-card', href: itemUrl, ref: cardRef, onClick: (e) => onClick(e, item, productSwatch?.selectedVariation?.variationId) },
            React.createElement("div", { className: 'cio-image-container', onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
                React.createElement("img", { alt: itemName, src: itemImageUrl, className: 'cio-image' }),
                rolloverImage && (React.createElement("img", { alt: `${itemName} rollover`, src: rolloverImage, loading: 'lazy', className: concatStyles('cio-image cio-rollover-image', isRolloverImageShown && 'is-active') }))),
            React.createElement("div", { className: 'cio-content' },
                React.createElement("div", { className: 'cio-item-prices-container' },
                    hasSalePrice && (React.createElement("div", { className: 'cio-item-price', id: 'cio-sale-price' }, formatPrice(salePrice))),
                    Number(itemPrice) >= 0 && (React.createElement("div", { className: concatStyles('cio-item-price', hasSalePrice && 'cio-item-price-strikethrough') }, formatPrice(itemPrice)))),
                React.createElement("div", { className: 'cio-item-name' }, itemName),
                productSwatch && React.createElement(ProductSwatch, { swatchObject: productSwatch })),
            React.createElement("button", { className: 'cio-add-to-cart-button', type: 'button', onClick: (e) => onAddToCart(e, item, itemPrice, productSwatch?.selectedVariation?.variationId) }, "Add to Cart"))));
}
